export function alignToChunkStart(segmentDurationsSec, positionSec) {
    // 처음부터이거나, 길이를 모르거나(빈 목록), 값이 이상하면 0 이다.
    if (!(positionSec > 0) || segmentDurationsSec.length === 0) {
        return { index: 0, startSec: 0, endSec: segmentDurationsSec[0] ?? 0 };
    }
    let acc = 0;
    for (let i = 0; i < segmentDurationsSec.length; i += 1) {
        const duration = segmentDurationsSec[i] ?? 0;
        const next = acc + duration;
        // **`<` 다.** 경계값(`4.0`)은 그 청크의 **시작**이므로 다음 청크에 속한다 —
        // `<=` 로 두면 경계에서 한 청크 앞으로 밀려 2초를 다시 본다.
        if (positionSec < next)
            return { index: i, startSec: acc, endSec: next };
        acc = next;
    }
    // 끝을 넘겼다 — **마지막 청크의 시작으로.** `0` 으로 되돌리지 않는다:
    // 다 본 회차를 목록에서 다시 고르면 처음으로 튄다 (`docs/38` §9 의 9).
    const last = segmentDurationsSec.length - 1;
    return { index: last, startSec: acc - (segmentDurationsSec[last] ?? 0), endSec: acc };
}
/**
 * 미드롤의 컷 앵커 — **정책상 초 → 청크 경계로 내린 mediaTime** (v21).
 *
 * ★ **어댑터 둘이 이 함수를 부른다.** 각자 계산하면 「hls.js 쪽만 고쳤다」가 되고,
 * 그러면 **그 기기에서만** 미드롤이 어긋난다 — 그리고 **CDP 가 iOS 에 붙지 않아 하네스가
 * 못 본다.** `alignToChunkStart` 를 하나로 둔 것과 같은 이유다 (절대 규칙 47).
 *
 * 돌려주는 것이 둘이다.
 * - `mediaTime` — 프레임 체인이 비교할 값. `videoStart` 만큼 밀려 있다
 * - `cutAtSec` — **콘텐츠 축의 초.** 코어에 되돌려 주면 코어가 그 값으로 복귀를 무장한다
 *
 * **`null` 을 돌려주는 셋** — 아직 매니페스트가 없거나(`segmentDurations`), 첫 프레임이
 * 합성되지 않았거나(`videoStart`), **컷 지점이 회차의 끝인 경우**다. 셋 다 「무장하지
 * 않는다」로 이어진다 — `onAbandoned` 로 흘리면 `recoverByLoad` 가 **회차를 통째로
 * 넘긴다**(광고 하나를 못 트는 대가로 드라마 한 편을 건너뛰는 셈이다).
 */
export function adCutAnchor(segmentDurationsSec, videoStart, videoSpan, atSec) {
    if (segmentDurationsSec === null || segmentDurationsSec.length === 0)
        return null;
    if (videoStart === null)
        return null;
    // **0 이하의 시점에는 미드롤이 서지 않는다.** `midrollCuePoints` 는 `interval` 부터
    // 시작하므로 여기 오지 않지만, 오면 **회차 시작 2초 뒤에 광고가 끼는** 셈이라 막는다.
    if (!(atSec > 0))
        return null;
    const { endSec } = alignToChunkStart(segmentDurationsSec, atSec);
    if (!(endSec > 0))
        return null;
    // **마지막 청크의 끝은 회차의 끝이다** — 미드롤이 설 자리가 아니고, 거기서는 왕복
    // 불변식도 성립하지 않는다 (이 파일 머리의 ★).
    if (videoSpan !== null && endSec >= videoSpan)
        return null;
    return { mediaTime: videoStart + endSec, cutAtSec: endSec };
}
/**
 * 탐색이 **끝난 뒤**의 프레임에서만 「위치 0 에 해당하는 mediaTime」을 낸다 (v27/TG16).
 *
 * ── ★ 2026-09-04 무대 신고의 뿌리 ────────────────────────────
 *
 * 어댑터는 `currentTime = alignedStartSec` 을 걸고 **바로 다음 합성 프레임**을 잡아
 * `videoStart = mediaTime − alignedStartSec` 으로 삼았다. 그런데 **탐색은 비동기라**
 * 그 프레임이 탐색 **전**의 것일 수 있다 — 이어보기 150초에서 실측한 값이
 * `mediaTime 0.021` 이었고, 그래서 `videoStart` 가 **−149.98** 이 됐다.
 *
 * 그 한 값이 회차의 끝을 옮긴다:
 *
 * ```
 *   lastFrameMediaTime = videoStart + videoSpan − 1/30
 *   정상  0.021 + 300.03 − 0.03 = 300.02   ← 회차 끝
 *   결함 −149.98 + 300.03 − 0.03 = 150.02   ← **이어보기 지점 그 자리**
 * ```
 *
 * 그래서 **붙자마자 꼬리 스왑이 터지고**, 코어가 `complete` 를 보내며 다음 회차로 넘어간다.
 * 시청자에게는 「이어보기가 사라지고 다음 회차가 처음부터 나온다」이고, 곧이어 주기 보고가
 * **그 회차의 1초로 저장된 지점을 덮는다 — 되돌릴 수 없다.**
 *
 * **광고가 켜진 채널에서 훨씬 자주 난다** — 광고가 같은 엘리먼트에서 먼저 돌아 프레임이
 * 이미 합성돼 있기 때문이다. 광고를 끄면 재현되지 않아 **광고 결함으로 보인다.**
 *
 * ── 왜 「위치 0」을 0 으로 눌러 두지 않는가 ────────────────────
 *
 * 처음부터 재생하면 그 값은 AAC 프라이밍(≈0.021)이다. 0 으로 두면 마지막 프레임 판정이
 * **한 프레임 일찍** 일어나는데, 그 방향은 안전하다(늦으면 채널이 선다). 그래서 이
 * 함수가 `null` 을 낼 때 어댑터는 **프레임을 몇 장 더 기다렸다가 0 으로 물러난다.**
 *
 * @returns 「위치 0 에 해당하는 mediaTime」. 탐색 전 프레임으로 보이면 `null`.
 */
export function videoStartFromFrame(mediaTimeSec, alignedStartSec) {
    if (!Number.isFinite(mediaTimeSec))
        return null;
    // 처음부터 재생하면 탐색이 없다 — 첫 프레임이 곧 그 값이다.
    if (!(alignedStartSec > 0))
        return mediaTimeSec;
    // 탐색이 끝난 프레임은 목표 지점 **이후**다. 그 전 프레임은 mediaTime 이 한참 작다.
    if (mediaTimeSec < alignedStartSec - SEEK_SETTLE_TOLERANCE_SEC)
        return null;
    return mediaTimeSec - alignedStartSec;
}
/**
 * 탐색이 끝났다고 볼 여유(초). **세그먼트 하나보다 작게 둔다** — 정렬이 이미 청크
 * 경계로 내렸으므로 프레임은 목표 **이후**에 오고, 이 값은 반올림과 프라이밍만 덮으면
 * 된다. 크게 잡으면 −150 짜리는 여전히 걸러 내지만 **작게 어긋난 것을 놓친다.**
 */
export const SEEK_SETTLE_TOLERANCE_SEC = 0.5;
/**
 * 프레임이 이만큼(ms) 오지 않으면 **프레임 체인이 죽었다**고 본다 (v33-e). 30fps 에서
 * 15장이다 — 보이는 창에서 이만큼 비는 것은 재생이 멈췄을 때뿐이고, 그때는 `currentTime`
 * 도 서 있어 아래 판정이 참이 되지 않는다.
 */
export const FRAME_STALE_MS = 500;
/**
 * ★ v33-e. **프레임 없이 컷 지점을 지났는가** — `timeupdate` 가 부른다 (2026-09-23 · `docs/68` §2.0-g).
 *
 * 컷(미드롤 · 라이브 진입)은 **프레임 콜백 안에서** 터진다 — 한 프레임도 틀리지 않게
 * 자르려면 그 자리여야 한다. 그런데 **보이지 않는 창에서 Chrome 은 프레임 콜백을 멈추고
 * `timeupdate` 는 계속 낸다.** 그러면 무장한 컷이 영영 안 터지고, 코어의 상한이 즉시형으로
 * 받아 **청크 경계가 아닌 자리**가 복귀 지점이 된다(절대 규칙 84 위반 · AWS 실측 177.17).
 * 프레임 콜백이 아예 없는 기기(네이티브 어댑터의 저하 모드)도 같다 — 거기서는 미드롤 컷이
 * 원리적으로 안 터졌다.
 *
 * 그래서 **프레임이 끊겼을 때만** `timeupdate` 로 컷 지점 통과를 판정한다. 판정은 콘텐츠
 * 축이다(`currentTime` ≥ 청크 경계). 정밀도는 `timeupdate`(약 4Hz)로 떨어지지만, 그
 * 길은 **아무도 화면을 안 보고 있거나**(숨은 창) 이미 무갭을 포기한 기기(저하 모드)다.
 * **되돌려 주는 컷 초는 무장할 때 구한 청크 경계 그대로다** — 왕복 불변식이 산다.
 *
 * - `cutAtSec` 이 없으면(회차 끝 꼬리) 거짓 — 그 길은 `ended` 백스톱이 받는다
 * - 프레임이 살아 있으면 거짓 — 프레임 체인이 정확한 자리에서 자른다(두 판정이 겹치지 않는다)
 */
export function cutDueWithoutFrames(input) {
    const { cutAtSec, currentTimeSec, lastFrameAtMs, nowMs } = input;
    if (cutAtSec === undefined || !Number.isFinite(currentTimeSec))
        return false;
    if (currentTimeSec < cutAtSec)
        return false;
    if (lastFrameAtMs === null)
        return true;
    return nowMs - lastFrameAtMs > (input.staleMs ?? FRAME_STALE_MS);
}
//# sourceMappingURL=chunk-align.js.map